import { useMemo } from 'react';
import { OptimusProvider } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { PaymentPlanPaymentTableRecord, useRepositoryQuery } from '@mews-commander/data-access';
import { TableHeader, StandaloneTable, useTableState, Stack } from '@optimus-web/core';
import { useGetPaymentPlanDetail } from '../PaymentPlans/queries';
import { WithPaymentPlanId } from '../PaymentPlans/types';
import { PaymentPlanDetailsOverviewTableHeaderActions } from './PaymentPlanDetailsOverviewTableHeaderActions';
import { getPaymentPlanDetailsOverviewTableMapping } from './paymentPlanDetailsOverviewTableMapping';

export const PaymentPlanDetailsOverviewTable = ({ paymentPlanId, accountId }: WithPaymentPlanId & WithAccountId) => {
    const [tableState, setTableState] = useTableState<PaymentPlanPaymentTableRecord>({
        pagination: {
            show: false,
        },
    });
    const paymentPlanDetailQuery = useGetPaymentPlanDetail({
        id: paymentPlanId,
    });

    const paymentPlansTableRecordsQuery = useRepositoryQuery('paymentPlans', 'getTableRecords', { id: paymentPlanId });

    const tableData = paymentPlansTableRecordsQuery.data ?? [];

    const columns = useMemo(
        () => getPaymentPlanDetailsOverviewTableMapping({ accountId, paymentPlanId }),
        [accountId, paymentPlanId]
    );

    return (
        <Stack vertical>
            <TableHeader
                title={lt('PaymentPlanDetailsOverviewTableTitle')}
                rightSide={
                    <PaymentPlanDetailsOverviewTableHeaderActions
                        paymentPlanId={paymentPlanId}
                        reservationId={paymentPlanDetailQuery.data?.reservationId}
                        accountId={accountId}
                        paymentMethodData={paymentPlanDetailQuery.data?.paymentMethodData}
                    />
                }
            />
            <OptimusProvider density="spacious">
                <StandaloneTable
                    columns={columns}
                    data={tableData}
                    uniqueRowId="id"
                    state={tableState}
                    onStateChange={setTableState}
                    amountOfRows={tableData.length}
                    loading={paymentPlanDetailQuery.isFetching || paymentPlansTableRecordsQuery.isFetching}
                    emptyState={{
                        primaryText: lt('PaymentPlanDetailsOverviewTableEmptyStatePrimaryText'),
                    }}
                />
            </OptimusProvider>
        </Stack>
    );
};
